/* eslint-disable react/no-danger */
import React, {useState} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {SearchBox} from '@wr/ui-patterns';
import {getIsWebView, getLanguageCode, getTranslations} from '../../../utils/selectors';
import {getProviders} from '../../../actions/trueLayer';
import {TrueLayerConfirmationModal} from '../../organisms';
import CancelationLink from '../../atoms/CancelationLink';
import ProviderButton from '../../atoms/TrueLayerProviderButton';
import assetUrl from '../../../utils/assetUrl';
import {interpolateTranslation} from '../../../utils/interpolateTranslation';
import {genericErrorHasOccurred} from '../../../actions/generic-error/action-creators';

const searchBoxIcon = assetUrl('images/search.svg');
const cancelUrlPath = 'transaction/payment-selection?status=failure';

const TrueLayerProviderSelection = ({
    providers, translations, languageCode, match, isWebView
}) => {
    const {sessionId: paymentSessionId} = match.params;

    const [filteredProviders, setFilteredProviders] = useState(providers);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [providersSearchInput, setProvidersSearchInput] = useState(null);
    const providerConfirmationPopupOpen = Boolean(selectedProvider);

    const providersFilterChange = searchInput => {
        const searchString = searchInput.toLowerCase();
        let newFilteredProviders = providers;
        if (searchString.length >= 2) {
            newFilteredProviders = providers.filter(p =>
                p.displayName.toLowerCase().includes(searchString));
        }
        setFilteredProviders(newFilteredProviders);
        setProvidersSearchInput(searchInput);
    };

    const clearSelectedProvider = () => setSelectedProvider(null);
    const noProvidersOnCorridor = providers.length === 0;
    const noProvidersFoundAfterSearch = !noProvidersOnCorridor && filteredProviders.length === 0;
    const noSearchResults = interpolateTranslation(translations.noSearchResults, {
        search: providersSearchInput
    });

    return (
        <section className="truelayer-provider-selection-container">
            <div className="truelayer-provider-selection-content">
                {!isWebView && (
                    <CancelationLink
                        cancellationUrl={`/${languageCode}/${cancelUrlPath}`}
                        analytics={undefined}
                    />
                )}
                <h1>{translations.chooseYourBank}</h1>
                <p className="banks-available">{translations.banksAvailable}</p>
                <SearchBox
                    placeholder={translations.search}
                    ariaLabel={translations.searchBank}
                    onChange={providersFilterChange}
                    iconUrl={searchBoxIcon}
                />
                {noProvidersOnCorridor && <p>No providers found on the current corridor</p>}
                {noProvidersFoundAfterSearch && (
                    <div className="no-providers-found">
                        <p dangerouslySetInnerHTML={{__html: noSearchResults}} />
                        <p>
                            {translations.searchSuggestionsTitle}
                        </p>
                        <ul>
                            {
                                translations.searchSuggestions.map(suggestion =>
                                    <li key={suggestion}>{suggestion}</li>)
                            }
                        </ul>
                    </div>
                )
                }
                <ul className="providers-list">
                    {filteredProviders.map(provider => (
                        <li
                            key={`${provider.providerId}-${provider.schemeId}`}
                            className="provider-list-item"
                        >
                            <ProviderButton
                                provider={provider}
                                onProviderClick={setSelectedProvider}
                            />
                        </li>
                    ))}
                </ul>
                <TrueLayerConfirmationModal
                    open={providerConfirmationPopupOpen}
                    onClose={clearSelectedProvider}
                    provider={selectedProvider}
                    paymentSessionId={paymentSessionId}
                />
            </div>
        </section>
    );
};

TrueLayerProviderSelection.getServerSideProps = async (context, store) => {
    try {
        const providers = await getProviders(
            context.requestId,
            context.params.sessionId,
            context.sessionId
        );
        return {providers};
    } catch (error) {
        store.dispatch(genericErrorHasOccurred(true));
        return {providers: []};
    }
};

TrueLayerProviderSelection.propTypes = {
    translations: PropTypes.shape({
        chooseYourBank: PropTypes.string.isRequired,
        banksAvailable: PropTypes.string.isRequired,
        search: PropTypes.string.isRequired,
        searchBank: PropTypes.string.isRequired,
        noSearchResults: PropTypes.string.isRequired,
        searchSuggestionsTitle: PropTypes.string.isRequired,
        searchSuggestions: PropTypes.arrayOf(PropTypes.string).isRequired
    }).isRequired,
    providers: PropTypes.arrayOf(
        PropTypes.shape({
            schemeId: PropTypes.string.isRequired,
            providerId: PropTypes.string.isRequired,
            displayName: PropTypes.string.isRequired,
            iconUrl: PropTypes.string.isRequired
        })
    ).isRequired,
    match: PropTypes.shape({
        params: PropTypes.shape({
            sessionId: PropTypes.string.isRequired
        }).isRequired
    }).isRequired,
    languageCode: PropTypes.string.isRequired,
    isWebView: PropTypes.bool.isRequired
};

const mapStateToProps = state => ({
    languageCode: getLanguageCode(state),
    translations: getTranslations(state).trueLayer,
    isWebView: getIsWebView(state)
});

export default connect(mapStateToProps)(TrueLayerProviderSelection);
